import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./themeUpdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";

const MAX_WORDS = 8;
const MAX_WORD_LEN = 13;
const MAX_HINT_LEN = 150;
const MIN_HINT_LEN = 10;
const HINT_REQUIRED_MODES = ["text", "maskedhint"];

const MODES = [
  { value: "normal", label: "Show Full Word" },
  { value: "masked", label: "Show Partial Word" },
  { value: "text", label: "Show Hint Only" },
  { value: "maskedhint", label: "Partial Word + Hint" },
];

function normalizeStringArray(val) {
  if (Array.isArray(val)) return val.map((s) => String(s ?? ""));
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      if (Array.isArray(p)) return p.map((s) => String(s ?? ""));
    } catch {
      /* fall through */
    }
    const regex = /s:\d+:"([^"]*)";/g;
    const out = [];
    let m;
    while ((m = regex.exec(val)) !== null) out.push(m[1] || "");
    if (out.length) return out;
  }
  return [];
}

function pairsFromTheme(data) {
  const words = normalizeStringArray(data?.words);
  const hints = normalizeStringArray(data?.wordpuzzle_words_indication);
  const n = Math.max(words.length, hints.length, 1);
  const rows = [];
  for (let i = 0; i < n; i++) {
    rows.push({
      word: (words[i] || "")
        .replace(/[^A-Za-z]/g, "")
        .slice(0, MAX_WORD_LEN),
      hint: (hints[i] || "").slice(0, MAX_HINT_LEN),
    });
  }
  return rows;
}

function isHintRequired(mode) {
  return HINT_REQUIRED_MODES.includes(mode);
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((s) => s.theme);

  const [rows, setRows] = useState([{ word: "", hint: "" }]);
  const [wordDisplayMode, setWordDisplayMode] = useState("normal");
  const [saving, setSaving] = useState(false);

  const themeId = currentTheme || data?.themename;
  const hintRequired = isHintRequired(wordDisplayMode);

  useEffect(() => {
    if (themeId) dispatch(fetchThemeData({ themeId }));
  }, [dispatch, themeId]);

  useEffect(() => {
    if (!data) return;
    setRows(pairsFromTheme(data));
    const m = data.word_display_mode;
    setWordDisplayMode(
      typeof m === "string" && m ? String(m).toLowerCase() : "normal"
    );
  }, [data]);

  const updateRow = (i, field, val) => {
    setRows((prev) => {
      const next = [...prev];
      if (field === "word") {
        next[i] = { ...next[i], word: val.replace(/[^A-Za-z]/g, "").slice(0, MAX_WORD_LEN) };
      } else {
        next[i] = { ...next[i], hint: val.slice(0, MAX_HINT_LEN) };
      }
      return next;
    });
  };

  const addRow = () => {
    if (rows.length >= MAX_WORDS) {
      Swal.fire("Limit", `You can add a maximum of ${MAX_WORDS} words only.`, "warning");
      return;
    }
    setRows([...rows, { word: "", hint: "" }]);
  };

  const removeRow = (i) => {
    if (rows.length <= 1) {
      Swal.fire("Required", "At least one word is required.", "warning");
      return;
    }
    setRows(rows.filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    const words = [];
    const hints = [];
    const seen = new Set();

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const rowNum = i + 1;
      const w = (r.word || "").trim();
      const h = (r.hint || "").trim();
      if (!w) {
        Swal.fire(
          "Validation",
          hintRequired
            ? `Row ${rowNum}: Word is required. Hint is required for this display mode.`
            : "Word is required for each row.",
          "error"
        );
        return;
      }
      if (w.length > MAX_WORD_LEN) {
        Swal.fire("Validation", `Row ${rowNum}: Each word must be at most ${MAX_WORD_LEN} letters.`, "error");
        return;
      }
      if (hintRequired) {
        if (!h) {
          Swal.fire(
            "Validation",
            `Row ${rowNum}: Hint is required when using Show Hint Only or Partial Word + Hint.`,
            "error"
          );
          return;
        }
        if (h.length < MIN_HINT_LEN) {
          Swal.fire(
            "Validation",
            `Row ${rowNum}: Hint must be at least ${MIN_HINT_LEN} characters.`,
            "error"
          );
          return;
        }
      }
      if (h.length > MAX_HINT_LEN) {
        Swal.fire("Validation", `Row ${rowNum}: Hint must be at most ${MAX_HINT_LEN} characters.`, "error");
        return;
      }
      const lower = w.toLowerCase();
      if (seen.has(lower)) {
        Swal.fire("Validation", "Duplicate words are not allowed.", "error");
        return;
      }
      seen.add(lower);
      words.push(w.toUpperCase());
      hints.push(h);
    }

    if (!wordDisplayMode) {
      Swal.fire("Validation", "Hint display mode is required.", "error");
      return;
    }

    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            words,
            wordpuzzle_words_indication: hints,
            word_display_mode: wordDisplayMode,
          },
          currentTheme: themeId,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId })).unwrap();
      Swal.fire("Success", "Data updated.", "success");
    } catch (e) {
      Swal.fire("Error", e?.message || "Failed to save.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="theme-update-page">
      <div className="back-button-holder">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/rules")}
        >
          <i className="fa-solid fa-angle-double-left" aria-hidden="true" /> BACK
        </button>
      </div>

      <div className="theme-update-card">
        <h2 className="theme-update-heading">Words</h2>
        <div className="theme-update-section">
          <h4 className="theme-update-subtitle">Word Indication Message</h4>
          <p className="theme-update-hint">
            <code>
              * Max {MAX_WORDS} words &nbsp;* Word length — {MAX_WORD_LEN} characters &nbsp;* Hint
              limit — {MAX_HINT_LEN} characters
              {hintRequired && (
                <>
                  {" "}
                  &nbsp;* Hint required — min {MIN_HINT_LEN} characters (current display mode)
                </>
              )}
            </code>
          </p>

          <div className="theme-update-rows">
            {rows.map((row, i) => (
              <div key={i} className="theme-update-row">
                <div className="theme-update-field">
                  <label>Word:</label>
                  <input
                    type="text"
                    className="theme-update-input"
                    placeholder="Word"
                    value={row.word}
                    maxLength={MAX_WORD_LEN}
                    onChange={(e) => updateRow(i, "word", e.target.value)}
                  />
                </div>
                <div className="theme-update-field">
                  <label>{hintRequired ? "Hint (required):" : "Hint (optional):"}</label>
                  <input
                    type="text"
                    className="theme-update-input"
                    placeholder="Indication"
                    value={row.hint}
                    maxLength={MAX_HINT_LEN}
                    minLength={hintRequired ? MIN_HINT_LEN : undefined}
                    required={hintRequired}
                    onChange={(e) => updateRow(i, "hint", e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="theme-update-remove"
                  onClick={() => removeRow(i)}
                  aria-label="Remove row"
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            ))}
          </div>

          <button type="button" className="theme-update-add-more" onClick={addRow}>
            Add More
          </button>
        </div>

        <div className="theme-update-mode-row">
          <h4 className="theme-update-subtitle">Hint Display Mode:</h4>
          <select
            className="theme-update-select"
            value={wordDisplayMode}
            onChange={(e) => setWordDisplayMode(e.target.value)}
          >
            {MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <div className="theme-update-actions">
          <button
            type="button"
            className="theme-update-save"
            disabled={saving || !themeId}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThemeUpdate;
